import { EditOutlined } from '@ant-design/icons';
import { Button, Card, Table, Switch } from 'antd';
import * as React from 'react';
import {ListBase, ListProps, Record, useDataProvider, useListContext, useNotify, usePermissions} from 'react-admin';
import { Link } from 'react-router-dom';
import { ExtendedDataProvider, RoutableTabConfig } from '@src/types';
import SkeletonTable, { SkeletonTableColumnsType } from '../Common/SkeletonTable';
import ResponseListView from "../Common/HTTPResponseList";
import {formatDateAndTimeFromNow} from "@src/helpers/utils";
import RoutableTabs from '../Common/RoutableTabs';
import { spinnerElement } from '../Common/utils';


const ListView = () => {
  const { ids, data, loading, total, perPage, setPage, page } = useListContext();
  const dataProvider: ExtendedDataProvider = useDataProvider();
  const notify = useNotify();
  const { permissions } = usePermissions();

  const triggerWebhook = (record: any) => {
    dataProvider.createAction('webhooks', { data: {}, id: record.id, verb: 'trigger/' })
        .then((response: any) => notify('Webhook triggered successfully'))
        .catch((error: any) => notify(error.body?.trigger, 'error'))
  }

  const enableWebhook = (record: any, checked: boolean) => {
    dataProvider.update('webhooks', {
      id: record.id,
      data: {
        active: checked ,
        service: record.service.id,
        target_url: record.target_url
      },
      previousData: { id: record.id }
    })
      .then(data => {
        record.active = checked
        notify('Webhook ' + (record.active? "Enabled": "Disabled"));
      })
      .catch((error: any) => {
        if (error.status == 400) {
          notify('Error updating webhook', 'error')
        }
      })
  }

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Service',
      dataIndex: ['service', 'name'],
      key: 'service',
    },
    {
      title: 'Change Instance State',
      dataIndex: 'change_instance_state',
      key: 'change_instance_state',
      render: (text: any) => text?text:"-",
      align: "center"
    },
    {
      title: 'Target URL',
      dataIndex: 'target_url',
      key: 'target_url',
      render: (text: any, record: any) => <div title={text} style={{ width: '150px', overflow: 'hidden', textOverflow: 'ellipsis' }}>{text}</div>
    },
    {
      render: (text: any, record: any) =>
        <div>
          {
              permissions === "ADMIN" &&
              <Button style={{ marginRight: '5px'}} onClick={() => triggerWebhook(record)}>
                Trigger
              </Button>
          }
          <Button style={{ marginRight: '5px'}}>
            <Link to={`/webhooks/${record.id}/edit`}><EditOutlined />{permissions === "ADMIN"?"Edit":"Show"}</Link>
          </Button>
          {
            permissions === "ADMIN" &&
            <Switch
              checkedChildren="Enabled"
              unCheckedChildren="Disabled"
              defaultChecked={record.active}
              onChange={(checked) => enableWebhook(record, checked)}
            />
          }
        </div>
    },

  ]

  const tableData: Record[] = []
  ids.forEach(id => {
    tableData.push(data[id])
  });
  if (loading) {
    return spinnerElement;
  }

  return (
    <Card
      bordered={false}
      className="criclebox tablespace mb-24"
    >
      <SkeletonTable loading={loading} columns={columns as SkeletonTableColumnsType[]}>
        <Table
          rowKey='id'
            // @ts-ignore
          columns={columns}
          pagination={{ total: total, pageSize: perPage, current: page, onChange: page => setPage(page), className: "pagination-padding" }}
          dataSource={tableData}
        />
      </SkeletonTable>
    </Card>
  )
}


const responses_columns = [
  {
    title: 'id',
    dataIndex: 'id',
    key: 'id',
  },
  {
    title: 'State',
    dataIndex: 'state',
    key: 'state',
  },
  {
    title: 'Status Code',
    dataIndex: 'status_code',
    key: 'status_code',
  },
  {
    title: 'Webhook',
    dataIndex: ['webhook', 'name'],
    key: 'webhook',
  },
  {
    title: 'Last Modified',
    key: 'modified',
    render: (text: any, record: any) => formatDateAndTimeFromNow(record.created)
  },
];


const WebhookList = (props: ListProps) => {
    const { permissions } = usePermissions();

    const handleTabChange = (activeKey: string) => {
        // console.log('Active tab changed to:', activeKey);
    };

    const tabs: RoutableTabConfig[] = [
        {
            key: 'webhooks',
            tab: <span>Webhooks</span>,
            content: <ListBase {...props}><ListView /></ListBase>,
        },
        {
            key: 'responses',
            tab: <span>Responses</span>,
            content: <ResponseListView columns={responses_columns} resource_name={"webhook_responses"} filters={{}}/>,
        },
    ];

    return (
        <div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginRight: '8px' }}>
                {permissions === "ADMIN" && <Link to="/webhooks/create"><Button type='primary'>Create Webhook</Button></Link>}
            </div>
            <RoutableTabs tabs={tabs} defaultActiveKey="webhooks" onTabChange={handleTabChange} />
        </div>
    );
};

export default WebhookList;
